// >>> REV-1: WHAT A REVIEW CANDIDATE IS, WHERE IT CAME FROM, AND WHAT MAY EVER BE SHOWN. <<<
//
// HER ORDER: harvest Elite 2026, the group threads and the Living Room; put an APPROVAL LEDGER on her
// dashboard (a three-tap desk, first names by default, provenance behind a toggle); and
// **>>> HER APPROVALS ARE THE GATE BEFORE ANY DISPLAY RENDERS. <<<**
//
// IT IMPORTS NOTHING, which is the entry requirement that lets a `.mjs` gate load and RUN every law
// in it (the same property `homeTapMap.js` and `readingRibbon.js` are built on). The SERVER composes
// from it too -- the harvest, the store and the owner routes all read these rules -- so it is a
// shared `.js` law rather than a `src/lib/*.ts`, which is CLIENT-ONLY (`strings.js`' own reason).

// ---------------------------------------------------------------------------------------------
// THE TWO SURFACE CLASSES, AND THE WHOLE DM PRIVACY LAW TURNS ON THIS ONE DISTINCTION (R18).
//
// A PUBLIC surface is one every member of this house can already read: a Living Room post, a
// comment under one. A PRIVATE surface is a conversation between people who believed they were
// alone: a DM, a group thread, Elite 2026. **They are harvested alike and they may NEVER be
// displayed alike.**
// >>> HER RECLASSIFICATION (2026-08-18): A 270-PERSON ROOM IS A CROWD, NOT A CONFIDENCE. <<<
// Her words: *"the ELITE 2026 GROUP THREAD is PUBLIC for review purposes, it is a 270+ member group
// chat, anyone speaking there spoke to a crowd, not in confidence."* So `lr-crowd` is a third class
// that sits with the PUBLIC ones for display, and `lr-message` keeps its meaning exactly: **true
// 1-on-1 DMs and small private threads remain private-class, approval-only, unchanged** -- her own
// sentence, and the reason this is a new class rather than a widening of the old one.
export const PUBLIC_SOURCES = Object.freeze(["lr-post", "lr-comment", "lr-crowd"]);
export const PRIVATE_SOURCES = Object.freeze(["lr-message", "lr-thread"]);

// >>> WHAT MAKES A THREAD A CROWD, AND IT IS THIS HOUSE'S OWN LAW RATHER THAN A NEW ONE. <<<
// CRYSTAL's `threadIsStandingRoom` already rules that a thread carrying a SLUG is a standing room
// this house named, and Elite 2026 is one (`slug: "elite-2026"`). A slug alone is not enough --
// a named thread of four people is not a crowd -- so a PARTICIPANT FLOOR rides with it.
//
// **THE FLOOR IS MINE RATHER THAN HERS AND IS DECLARED AS SUCH (R17/R160).** Her sentence names 270+
// and this is set well below it, because the question is *did she speak to a crowd* rather than *did
// she speak to 270 people*; it is one constant and it is reversible in one line. Measured today it
// resolves to exactly one thread, which is exactly the one she named.
export const CROWD_MIN_PARTICIPANTS = 25;

export function threadIsCrowd({ slug = "", participants = 0 } = {}) {
  return Boolean(String(slug || "").trim()) && Number(participants) >= CROWD_MIN_PARTICIPANTS;
}
export const REVIEW_SOURCES = Object.freeze([...PUBLIC_SOURCES, ...PRIVATE_SOURCES]);

/**
 * Is this surface one the whole house could already read?
 *
 * >>> AN UNROSTERED SOURCE IS PRIVATE, AND THAT DIRECTION IS THE RULING (R19/R8-4). <<< A surface
 * added next year that nobody classified must not become publishable by default -- reading it as
 * public would put a stranger's private words on her sales page with nothing red anywhere, which is
 * the exact harm her DM privacy law exists to prevent. Reading it as private costs one extra tap.
 */
export function sourceIsPublic(source) {
  return PUBLIC_SOURCES.includes(String(source || ""));
}

// ---------------------------------------------------------------------------------------------
// THE FOUR STATES, AND `provisional` IS HER OWN WORD (R18).
//
//   pending      -- harvested, waiting for her. Shows NOWHERE.
//   provisional  -- a CLEARLY-SAFE PUBLIC candidate, seeded so her page is not empty tonight.
//                   Shows, and is marked, and is still on her desk in the morning.
//   approved     -- her tap. Shows.
//   skipped      -- her tap. Shows nowhere, ever, and is not offered again.
//
// Her sentence is *"seed the display with the clearly-safe public Living Room candidates marked
// PROVISIONAL pending her morning approvals"* -- so `provisional` is a real state rather than a
// synonym for approved, and the desk can tell the two apart when she works through them.
export const REVIEW_STATES = Object.freeze(["pending", "provisional", "approved", "skipped"]);

/**
 * >>> MAY THIS ROW BE SHOWN TO ANYBODY? THIS IS THE ONE GATE EVERY DISPLAY SURFACE ASKS. <<<
 *
 * Her home carousel, the membership page's three slots and the desktop review rail all read the SAME
 * answer, because three readers of *may this be shown* is three chances for one of them to publish a
 * DM (R28/R29).
 *
 * **A PRIVATE-SURFACE ROW IS SHOWABLE ONLY WHEN SHE HAS APPROVED IT HERSELF.** `provisional` is not
 * enough and can never be enough: the whole point of that state is that nobody has looked yet.
 */
export function reviewShowable(row = {}) {
  const state = String(row.state || "");
  if (state === "approved") return true;
  if (state !== "provisional") return false;
  // Provisional is the seeded state, and it is PUBLIC-ONLY by construction.
  return sourceIsPublic(row.source);
}

/**
 * What the harvest is allowed to seed. Public only, and only when the candidate is clean enough that
 * seeding it needs no judgement -- which is what "clearly-safe" means in her sentence.
 */
export function seedState(row = {}) {
  return sourceIsPublic(row.source) && candidateIsClean(row) ? "provisional" : "pending";
}

// ---------------------------------------------------------------------------------------------
// WHAT MAKES A SENTENCE A CANDIDATE.
//
// This is a HARVEST rather than a judgement: it casts wide and SHE decides. The one thing it must
// never do is put something in front of her that cannot be shown at all, because a desk full of
// noise is a desk she stops working through -- which is how her approvals stop happening and every
// display surface stays empty.

// Somebody speaking WELL of something. Deliberately plain: this is a net, not a classifier.
const PRAISE = [
  "love", "loved", "loving", "amazing", "incredible", "accurate", "spot on", "blown away",
  "thank you", "thankful", "grateful", "changed my life", "life changing", "obsessed",
  "best", "beautiful", "powerful", "helped me", "helping me", "worth it", "so good",
  "on point", "wow", "chills", "goosebumps", "exactly", "needed this", "resonates",
  "resonated", "truth", "real", "legit", "works", "worked", "recommend",
];

// The thing being praised has to be OF THIS HOUSE, or a member praising her own dinner lands on the
// desk. Kept broad, because her members name the rooms and the readings a dozen ways.
const SUBJECTS = [
  "reading", "readings", "chart", "tarot", "card", "cards", "seal", "spell", "spells",
  "lala", "12th house", "12thhouse", "twelfth house", "numerology", "human design",
  "astrology", "palm", "dream", "cauldron", "petition", "membership", "room", "house",
  "guidance", "report", "spirit", "candle", "oil",
];

const has = (text, words) => {
  const t = ` ${String(text || "").toLowerCase()} `;
  return words.some((w) => t.includes(` ${w} `) || t.includes(` ${w},`) || t.includes(` ${w}.`) || t.includes(` ${w}!`));
};

export const REVIEW_MIN_CHARS = 24;
export const REVIEW_MAX_CHARS = 320;

/**
 * Does this body read as somebody saying something good about this house?
 *
 * BOTH HALVES ARE REQUIRED and that is the whole net: praise with no subject is a member being kind
 * to another member, and a subject with no praise is an ordinary question about a reading. Neither
 * belongs on her desk.
 */
export function looksLikeReview(body) {
  const text = String(body || "").trim();
  if (text.length < REVIEW_MIN_CHARS) return false;
  return has(text, PRAISE) && has(text, SUBJECTS);
}

/**
 * >>> CLEAN ENOUGH TO SEED WITHOUT HER LOOKING. <<<
 *
 * A candidate she has to think about is `pending`; this is the narrower question of whether it can
 * go up tonight with nobody having read it. So it refuses anything carrying another person's name,
 * an address, a number that could be a phone or an order, a link, or a question -- because a
 * question on a sales page reads as a complaint, and everything else on that list is somebody's
 * private detail.
 */
// >>> HER CONTENT FENCE (2026-08-18). IT GOVERNS **ALL** PROVISIONAL SEEDING, WHATEVER THE SURFACE.
// <<<
//
// HER WORDS: *"a quote may seed provisionally ONLY if it carries NO personal situation, no names of
// exes or family, no health, no legal, no money troubles, no relationship specifics, nothing a person
// would not want strangers reading with their name near it. Testimonial-class praise only."*
//
// **THIS IS A SECOND, INDEPENDENT WALL AND THAT IS THE STRUCTURAL POINT.** The surface class decides
// what may EVER display without her tap; the fence decides what may display TONIGHT without her
// having read it. A quote from a 270-person room can still be somebody's marriage, and so can a
// Living Room post -- so BOTH have to pass, and the fence is asked of the WORDS alone.
//
// IT REFUSES BY VOCABULARY AND IT REFUSES GENEROUSLY (R19/R8-4). A testimonial wrongly held costs one
// line on a page that has plenty; a situation wrongly seeded puts a member's health or her marriage
// in front of strangers **with her name beside it**, which is the harm her fence exists to prevent
// and which no error state anywhere would report.
const SITUATIONAL = [
  // relationships and family, including the exes and the names she calls out
  "my ex", "his ex", "her ex", "my husband", "my wife", "my partner", "my boyfriend", "my girlfriend",
  "my son", "my daughter", "my mother", "my father", "my mom", "my dad", "my sister", "my brother",
  "my aunt", "my uncle", "my cousin", "my grandmother", "my grandfather", "my nephew", "my niece",
  "my kids", "my children", "my baby", "my family", "divorce", "divorced", "breakup", "broke up",
  "cheated", "cheating", "affair", "custody", "pregnant", "pregnancy", "miscarriage",
  // health
  "cancer", "diagnosis", "diagnosed", "surgery", "hospital", "illness", "sick", "disease",
  "depression", "depressed", "anxiety", "medication", "therapy", "therapist", "addiction", "sober",
  "died", "death", "passed away", "funeral", "grief",
  // legal
  "lawyer", "attorney", "court", "lawsuit", "sued", "arrested", "jail", "prison", "probation",
  "restraining order", "custody battle", "eviction", "evicted",
  // money troubles
  "broke", "bankrupt", "bankruptcy", "debt", "in debt", "behind on", "cant afford", "can't afford",
  "unemployed", "laid off", "fired", "foreclosure", "garnish", "collections", "overdraft",
  // the shape of a personal situation being told
  "my situation", "going through", "struggling", "struggle", "my problem", "my issue",
];

/**
 * Does this quote carry a personal SITUATION rather than a testimonial?
 *
 * Matched on WORD BOUNDARIES, because a substring test is how `broke` convicts `broken-in` and
 * `sick` convicts `sickening` -- and over-refusing here is safe while over-ADMITTING is the harm, so
 * the boundaries are for precision rather than for leniency (a phrase is still matched whole).
 */
export function carriesSituation(body) {
  const t = ` ${String(body || "").toLowerCase().replace(/[^\p{L}\p{N}'\s]/gu, " ").replace(/\s+/g, " ")} `;
  return SITUATIONAL.some((w) => t.includes(` ${w} `));
}

/**
 * >>> A CAPITALISED NAME THAT IS NOT THIS HOUSE'S OWN IS SOMEBODY. <<<
 *
 * Her fence names *"no names of exes or family"*, and the surest form of that is a proper noun in the
 * middle of a sentence. The house's own words are exempt (they are the SUBJECT of a testimonial), and
 * so is the first word of a sentence, which is capitalised for grammar rather than for a person.
 */
const HOUSE_WORDS = new Set(["lala", "12th", "12thhouse", "house", "god", "jesus", "spirit", "i", "im", "i'm", "ive", "i've", "id", "i'd", "ill", "i'll", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday", "january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"]);

export function namesAPerson(body) {
  const text = String(body || "");
  // split into sentences so the first word of each is exempt
  for (const sentence of text.split(/(?<=[.!?\n])\s+/)) {
    const words = sentence.trim().split(/\s+/);
    for (let i = 1; i < words.length; i += 1) {
      const w = words[i].replace(/[^\p{L}']/gu, "");
      if (w.length < 3) continue;
      if (!/^\p{Lu}\p{Ll}+$/u.test(w)) continue;
      if (HOUSE_WORDS.has(w.toLowerCase())) continue;
      return true;
    }
  }
  return false;
}

/** The longest a quote may be and still be read as a testimonial rather than a story. */
export const TESTIMONIAL_MAX_CHARS = 200;

export function candidateIsClean(row = {}) {
  const text = String(row.body || "");
  if (text.length < REVIEW_MIN_CHARS || text.length > REVIEW_MAX_CHARS) return false;
  if (/[?]/.test(text)) return false;
  if (/@[A-Za-z0-9]/.test(text)) return false;          // an @tag names another member
  if (/https?:\/\/|www\./i.test(text)) return false;    // a link
  if (/\d{4,}/.test(text)) return false;                // an order number, a phone, a date of birth
  if (/\b[\w.+-]+@[\w-]+\.[\w.]+\b/.test(text)) return false; // an address
  // >>> HER CONTENT FENCE, ASKED OF EVERY SEED WHATEVER THE SURFACE. <<<
  if (text.length > TESTIMONIAL_MAX_CHARS) return false;   // a story rather than a testimonial
  if (carriesSituation(text)) return false;                // health, legal, money, family, a situation
  if (namesAPerson(text)) return false;                    // somebody's name beside her own
  return looksLikeReview(text);
}

// ---------------------------------------------------------------------------------------------
// HER DESK.

// >>> HER THREE TAPS AND NOTHING ELSE IN HER FACE. <<< A fourth control on this desk is a fourth
// decision per row, and there are hundreds of rows.
export const DESK_ACTIONS = Object.freeze([
  { key: "approve", label: "Approve", to: "approved" },
  { key: "trim", label: "Edit & approve", to: "approved" },
  { key: "skip", label: "Skip", to: "skipped" },
]);

export function deskActionValid(key) {
  return DESK_ACTIONS.some((a) => a.key === String(key || ""));
}

export function stateAfter(key) {
  const a = DESK_ACTIONS.find((x) => x.key === String(key || ""));
  return a ? a.to : null;
}

/**
 * >>> FIRST NAMES BY DEFAULT, HER WORD. <<<
 *
 * The room already resolves a member to a ROOM FIRST-NAME (LR1 ruling 3: the only name any surface
 * renders, and `publicIdentity` has no field a surname or an address could ride in). This takes the
 * FIRST TOKEN of that, because a room name is sometimes two words and a review on a sales page is
 * attributed the way a person introduces herself.
 *
 * A name this cannot reduce answers "" rather than a guess, and a row with no name is attributed to
 * nobody rather than to "Anonymous" -- an invented attribution is a false receipt about a real person
 * (R141).
 */
export function displayName(name) {
  const first = String(name || "").trim().split(/\s+/)[0] || "";
  // >>> AND THE POSSESSIVE COMES OFF, WHICH THE FIRST RENDERED RAIL IS WHY. <<<
  //
  // This house's room names are "X's room", so the FIRST TOKEN of one is "Stacie's" -- and the rail
  // attributed her words to **STACIE'S**, which is not a person's name and reads as a typo on the one
  // surface that quotes a member by name to strangers her advertising paid for.
  //
  // Only a real apostrophe-s is taken, straight or curly, so a name that merely ENDS in s -- Chris,
  // Jess, Lars -- is untouched, and a token that is nothing but a possessive answers "" rather than a
  // bare apostrophe. The curly form is in the class as well as the pattern because her members type
  // on phones, where the keyboard produces it by default.
  const bare = first.replace(/['’]s$/u, "");
  return /^[\p{L}][\p{L}'’-]*$/u.test(bare) ? bare : "";
}

/**
 * What a display surface renders. ONE composer, because her home carousel, the membership page and
 * the desktop rail must quote a member identically (R28/R29) -- and the quotation marks are HER
 * spec, so they belong to the law rather than to three stylesheets.
 */
export function reviewQuote(row = {}) {
  const body = String(row.trimmed || row.body || "").trim();
  if (!body) return null;
  const at = row.created_at ? new Date(row.created_at) : null;
  return { quote: `"${body}"`, name: displayName(row.author_name || row.name), provisional: String(row.state) === "provisional", at: at && !Number.isNaN(at.getTime()) ? at.toISOString() : "" };
}
